import React, { useState } from 'react';
import { MapPin, Mail, Clock } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { trackEvent } from '../analytics';

const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    projectType: '',
    quantity: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(formData.subject || t('contact.mailSubject'));
    const body = encodeURIComponent(
      `${t('contact.mailBodyName')}: ${formData.firstName} ${formData.lastName}\n${t('contact.mailBodyEmail')}: ${formData.email}\n${t('contact.mailBodyPhone')}: ${formData.phone}\n${t('contact.mailBodyCompany')}: ${formData.company}\n${t('contact.mailBodyCountry')}: ${formData.country}\n${t('contact.mailBodyProject')}: ${formData.projectType}\n${t('contact.mailBodyQty')}: ${formData.quantity}\n\n${formData.message}`
    );
    window.location.href = `mailto:info@hestiadoors.com?subject=${subject}&body=${body}`;
    trackEvent('contact_form_submit', { project_type: formData.projectType || 'unspecified' });
    setStatus('sent');
  };

  const sectionStyle = {
    padding: '5rem 1.5rem',
    position: 'relative'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '4rem'
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1.5rem'
  };

  const highlightStyle = {
    color: '#dfa926'
  };

  const descriptionStyle = {
    fontSize: '1.25rem',
    color: '#d1d5db',
    maxWidth: '32rem',
    margin: '0 auto'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
    gap: '3rem'
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)'
  };

  const cardTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '2rem',
    textAlign: 'left'
  };

  const contactItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    marginBottom: '1.5rem'
  };

  const iconStyle = {
    width: '1.5rem',
    height: '1.5rem',
    color: '#dfa926',
    flexShrink: 0,
    marginTop: '0.25rem'
  };

  const contactTitleStyle = {
    color: 'white',
    fontWeight: '600',
    marginBottom: '0.25rem',
    textAlign: 'left'
  };

  const contactTextStyle = {
    color: '#d1d5db',
    lineHeight: '1.6',
    textAlign: 'left'
  };

  const whyContactStyle = {
    background: 'linear-gradient(135deg, rgba(223, 169, 38, 0.1) 0%, rgba(223, 169, 38, 0.05) 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    border: '1px solid rgba(223, 169, 38, 0.2)',
    marginTop: '2rem'
  };

  const listStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const listItemStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.75rem',
    color: '#d1d5db'
  };

  const bulletStyle = {
    width: '0.5rem',
    height: '0.5rem',
    backgroundColor: '#dfa926',
    borderRadius: '50%',
    marginRight: '0.75rem'
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  };

  const formRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    color: 'white',
    fontWeight: '500',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    backgroundColor: 'rgba(58, 58, 58, 0.5)',
    border: '1px solid #4b5563',
    color: 'white',
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    transition: 'border-color 0.3s'
  };

  const textareaStyle = {
    ...inputStyle,
    resize: 'vertical',
    minHeight: '120px'
  };

  const buttonStyle = {
    width: '100%',
    backgroundColor: '#dfa926',
    color: 'black',
    fontWeight: '600',
    padding: '1rem',
    borderRadius: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  return (
    <section style={sectionStyle}>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {t('contact.titlePre')} <span style={highlightStyle}>{t('contact.titleHl')}</span>
          </h2>
          <p style={descriptionStyle}>
            {t('contact.desc')}
          </p>
        </div>

        <div style={gridStyle}>
          {/* Contact Information */}
          <div>
            <div style={cardStyle}>
              <h3 style={cardTitleStyle}>{t('contact.infoTitle')}</h3>

              <div>
                <div style={contactItemStyle}>
                  <MapPin style={iconStyle} />
                  <div>
                    <h4 style={contactTitleStyle}>{t('contact.address')}</h4>
                    <p style={contactTextStyle}>
                      Hestia Doors OÜ<br />
                      Päikese 1<br />
                      30221 Kohtla-Järve<br />
                      Estonia
                    </p>
                  </div>
                </div>

                <div style={contactItemStyle}>
                  <Mail style={iconStyle} />
                  <div>
                    <h4 style={contactTitleStyle}>{t('contact.email')}</h4>
                    <p style={contactTextStyle}>info@hestiadoors.com</p>
                  </div>
                </div>

                <div style={contactItemStyle}>
                  <Clock style={iconStyle} />
                  <div>
                    <h4 style={contactTitleStyle}>{t('contact.hours')}</h4>
                    <p style={contactTextStyle}>
                      {t('contact.hoursWeekday')}<br />
                      {t('contact.hoursSaturday')}<br />
                      {t('contact.hoursSunday')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div style={whyContactStyle}>
              <h3 style={{ ...cardTitleStyle, fontSize: '1.25rem' }}>{t('contact.whyTitle')}</h3>
              <ul style={listStyle}>
                {t('contact.whyBullets').map((item, i) => (
                  <li key={i} style={listItemStyle}>
                    <span style={bulletStyle}></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div style={cardStyle}>
            <h3 style={cardTitleStyle}>{t('contact.formTitle')}</h3>

            <form style={formStyle} onSubmit={handleSubmit}>
              <div style={formRowStyle}>
                <div>
                  <label htmlFor="firstName" style={labelStyle}>{t('contact.firstName')}</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder={t('contact.firstNamePh')}
                    onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                    onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" style={labelStyle}>{t('contact.lastName')}</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder={t('contact.lastNamePh')}
                    onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                    onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" style={labelStyle}>{t('contact.emailLabel')}</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder={t('contact.emailPh')}
                  onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                  onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                />
              </div>

              <div>
                <label htmlFor="phone" style={labelStyle}>{t('contact.phone')}</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="+372 XXXX XXXX"
                  onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                  onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                />
              </div>

              <div style={formRowStyle}>
                <div>
                  <label htmlFor="company" style={labelStyle}>{t('contact.company')}</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder={t('contact.companyPh')}
                    onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                    onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                  />
                </div>
                <div>
                  <label htmlFor="country" style={labelStyle}>{t('contact.country')}</label>
                  <input
                    id="country"
                    name="country"
                    type="text"
                    value={formData.country}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder={t('contact.countryPh')}
                    onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                    onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                  />
                </div>
              </div>

              <div style={formRowStyle}>
                <div>
                  <label htmlFor="projectType" style={labelStyle}>{t('contact.projectType')}</label>
                  <input
                    id="projectType"
                    name="projectType"
                    type="text"
                    value={formData.projectType}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder={t('contact.projectTypePh')}
                    onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                    onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                  />
                </div>
                <div>
                  <label htmlFor="quantity" style={labelStyle}>{t('contact.quantity')}</label>
                  <input
                    id="quantity"
                    name="quantity"
                    type="text"
                    value={formData.quantity}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder={t('contact.quantityPh')}
                    onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                    onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" style={labelStyle}>{t('contact.subject')}</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder={t('contact.subjectPh')}
                  onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                  onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                />
              </div>

              <div>
                <label htmlFor="message" style={labelStyle}>{t('contact.message')}</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  style={textareaStyle}
                  placeholder={t('contact.messagePh')}
                  onFocus={(e) => e.target.style.borderColor = '#dfa926'}
                  onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                ></textarea>
              </div>

              <button
                type="submit"
                style={buttonStyle}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#c8941f';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#dfa926';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                {t('contact.send')}
              </button>

              {status === 'sent' && (
                <p style={{ color: '#dfa926', textAlign: 'center', margin: 0, fontSize: '0.9rem' }}>
                  {t('contact.sent')}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
